import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useLazyGetProposalTeamReuseQuery } from "work/proposals/drafts-from-previous/_api/proposal-team-reuse-api";
import { fetchIndividualNames } from "work/proposals/drafts-from-previous/fetch-individual-names";
import ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import { startDraftFromPreviousProposal } from "work/proposals/drafts-from-previous/start-draft-from-previous-proposal";
import type Proposal from "work/proposals/proposal";

/**
 * "Create from previous": opens a new, unsaved draft pre-filled from a proposal (#1569).
 *
 * `isStarting` is true while it asks the server who on the proposal can be carried over, so the
 * control that started it can show that it is working and cannot be pressed twice.
 */
export default function useStartDraftFromPreviousProposal() {
  const dispatch = useAttorneyHubDispatch();
  const navigate = useNavigate();
  const session = useSession();
  const [getTeamReuse] = useLazyGetProposalTeamReuseQuery();
  const [isStarting, setIsStarting] = useState(false);

  async function start(proposal: Proposal): Promise<boolean> {
    if (isStarting) return false;
    setIsStarting(true);
    const started = await startDraftFromPreviousProposal({
      proposal,
      session,
      dispatch,
      navigate,
      fetchTeamReuse: async (proposalId: Guid) =>
        ProposalTeamReuse.fromJSON(await getTeamReuse({ proposalId: proposalId.value }).unwrap()),
      fetchNames: (userIds: Set<string>) => fetchIndividualNames(dispatch, userIds),
    });
    setIsStarting(false);
    return started;
  }

  return { start, isStarting };
}
